import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Link, Navigate } from 'react-router-dom';
import {
  Box,
  CssBaseline,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Container,
  useTheme,
  useMediaQuery,
  ThemeProvider,
  Avatar,
  Menu,
  MenuItem,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  MenuBook as MenuBookIcon,
  Build as BuildIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Business as BusinessIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  People as PeopleIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  Storage as StorageIcon,
  ListAlt as ListAltIcon
} from '@mui/icons-material';
import Dashboard from './pages/Dashboard';
import Executions from './pages/Executions';
import Operations from './pages/Operations';
import Maintenance from './pages/Maintenance';
import Admin from './pages/Admin';
import Tenants from './pages/Tenants';
import Runbooks from './pages/Runbooks';
import Assets from './pages/Assets';
import Login from './pages/Login';
import Invite from './pages/Invite';
import InviteCallback from './pages/InviteCallback';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import McpAuthorize from './pages/McpAuthorize';
import api, { User } from './services/api';
import { PermissionProvider, usePermissions } from './contexts/PermissionContext';
import { TenantProvider, useTenant } from './contexts/TenantContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { TenantSelector } from './components/TenantSelector';
import { TenantSelectionView } from './components/TenantSelectionView';
import { ActiveAttackBar } from './components/ActiveAttackBar';
import getCaosBlitzTheme from './theme';
import { ThemeModeProvider, useThemeMode } from './contexts/ThemeContext';
import { TimezoneProvider, useTimezone } from './contexts/TimezoneContext';
import NotificationPanel from './components/NotificationPanel';
import TimezoneSelectorDialog from './components/TimezoneSelectorDialog';
import { formatTimezoneAbbrevAndOffset } from './utils/dateUtils';
import EnvironmentBadge from './components/EnvironmentBadge';
import { getDocumentTitle } from './utils/envLabel';

const TOKEN_REFRESH_INTERVAL_MS = 20 * 60 * 1000; // 20 minutes

const drawerWidth = 240;
const miniDrawerWidth = 72;

interface MainContentProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  user: User | null;
  onLogout: () => void;
}

const MainContent: React.FC<MainContentProps> = ({ open, setOpen, user, onLogout }) => {
  const location = useLocation();
  const theme = useTheme();
  // Use a narrower mobile breakpoint to avoid accidental modal backdrop blocking on desktop widths.
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [timezoneDialogOpen, setTimezoneDialogOpen] = useState(false);
  const { hasPermission, isAdmin, permissions } = usePermissions();
  const { mode, toggleTheme } = useThemeMode();
  const { timezone, timezoneOverride, browserTimezone, setTimezone } = useTimezone();
  const timezoneMetaLabel = formatTimezoneAbbrevAndOffset(timezone);

  useEffect(() => {
    // Prevent mobile temporary drawer backdrop from blocking page interactions on load.
    if (isMobile && open) {
      setOpen(false);
    }
  }, [isMobile, open, setOpen]);

  // Close profile menu and mobile drawer whenever the route changes.
  // Without this, navigating via the sidebar leaves the Menu modal backdrop
  // stuck in the DOM (z-index 1300) blocking all page interaction.
  useEffect(() => {
    setAnchorEl(null);
    if (isMobile) setOpen(false);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const menuItems = [
    {
      text: 'Operations',
      icon: <SettingsIcon />,
      path: '/operations',
      permission: 'access_operations'
    },
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/',
      permission: 'view_dashboard'
    },
    {
      text: 'Executions',
      icon: <ListAltIcon />,
      path: '/executions',
      permission: 'view_operations'
    },
    {
      text: 'Runbooks',
      icon: <MenuBookIcon />,
      path: '/runbooks',
      permission: 'view_runbooks'
    },
    {
      text: 'Assets',
      icon: <StorageIcon />,
      path: '/assets',
      permission: 'view_assets'
    },
    {
      text: 'Maintenance',
      icon: <BuildIcon />,
      path: '/maintenance',
      permission: 'access_maintenance',
      globalAdminOnly: true
    },
    {
      text: 'Admin',
      icon: <BusinessIcon />,
      path: '/partners',
      permission: 'view_admin_panel'
    },
    {
      text: 'Tenants',
      icon: <PeopleIcon />,
      path: '/tenants',
      permission: 'manage_tenants',
      partnerAdminOnly: true
    },
  ];

  // Filter menu items based on permissions
  const visibleMenuItems = menuItems.filter(item => {
    if (item.globalAdminOnly) {
      return isAdmin;
    }
    if (item.partnerAdminOnly) {
      // Show for partner admins (not global admins)
      return !isAdmin && hasPermission(item.permission);
    }
    // Regular permission check
    return isAdmin || hasPermission(item.permission);
  });

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    onLogout();
  };

  // Get user's highest role
  const getUserRole = () => {
    if (isAdmin) return 'Global Admin';
    if (permissions?.partner_permissions && permissions.partner_permissions.length > 0) {
      const highestPartnerRole = permissions.partner_permissions.reduce((highest, perm) => {
        const roleHierarchy = { viewer: 1, operator: 2, admin: 3 };
        // Add null checks for role property
        if (!perm.role || !highest.role) return highest;
        return roleHierarchy[perm.role] > roleHierarchy[highest.role] ? perm : highest;
      }, permissions.partner_permissions[0]); // Provide initial value
      
      // Add null check before accessing role
      if (highestPartnerRole?.role && highestPartnerRole?.partner_name) {
        return `${highestPartnerRole.role.charAt(0).toUpperCase() + highestPartnerRole.role.slice(1)} - ${highestPartnerRole.partner_name}`;
      }
    }
    if (permissions?.tenant_permissions && permissions.tenant_permissions.length > 0) {
      const highestTenantRole = permissions.tenant_permissions.reduce((highest, perm) => {
        const roleHierarchy = { viewer: 1, operator: 2, admin: 3 };
        // Add null checks for role property
        if (!perm.role || !highest.role) return highest;
        return roleHierarchy[perm.role] > roleHierarchy[highest.role] ? perm : highest;
      }, permissions.tenant_permissions[0]); // Provide initial value
      
      // Add null check before accessing role
      if (highestTenantRole?.role && highestTenantRole?.tenant_name) {
        return `${highestTenantRole.role.charAt(0).toUpperCase() + highestTenantRole.role.slice(1)} - ${highestTenantRole.tenant_name}`;
      }
    }
    return 'No Permissions';
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { 
            xs: '100%',
            md: `calc(100% - ${open ? drawerWidth : miniDrawerWidth}px)` 
          },
          ml: { 
            xs: 0,
            md: `${open ? drawerWidth : miniDrawerWidth}px` 
          },
          backgroundColor: theme.palette.mode === 'dark' 
            ? theme.palette.background.paper
            : theme.palette.background.default,
          color: theme.palette.text.primary,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 2px 8px rgba(0,0,0,0.3)'
            : '0 2px 8px rgba(0,0,0,0.08)',
          borderBottom: `1px solid ${theme.palette.divider}`,
          zIndex: theme.zIndex.drawer + 3,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: 2, 
              display: { md: 'none' },
              color: theme.palette.text.primary
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
              flexGrow: 1, 
              fontWeight: 600,
              color: theme.palette.text.primary
            }}
          >
            CaosBlitz Control Panel
          </Typography>
          
          {/* Theme Toggle */}
          <IconButton 
            onClick={toggleTheme} 
            sx={{ 
              mr: 1,
              color: theme.palette.text.primary,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              }
            }}
            title={`Switch to ${mode === 'dark' ? 'light' : 'dark'} mode`}
          >
            {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>
          
          {/* Tenant Selector */}
          <Box sx={{ mr: 2, display: { xs: 'none', md: 'block' } }}>
            <TenantSelector />
          </Box>
          
          {/* User Profile Menu */}
          {user && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box
                sx={{
                  mr: 1,
                  display: { xs: 'flex', sm: 'none' },
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  maxWidth: 112,
                }}
              >
                <Typography
                  variant="caption"
                  component="button"
                  type="button"
                  title={`${timezone} (${timezoneMetaLabel})`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setTimezoneDialogOpen(true);
                  }}
                  sx={{
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    textUnderlineOffset: 2,
                    color: 'primary.main',
                    fontSize: '0.7rem',
                    fontFamily: 'inherit',
                    background: 'none',
                    border: 'none',
                    p: 0,
                    textAlign: 'right',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                  }}
                >
                  {timezone.includes('/') ? timezone.split('/').slice(-2).join('/') : timezone}{' '}
                  <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    ({timezoneMetaLabel})
                  </Box>
                </Typography>
              </Box>
              <Box sx={{ mr: 2, display: { xs: 'none', sm: 'block' }, textAlign: 'right' }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 500,
                    color: theme.palette.text.primary
                  }}
                >
                  {user.name}
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: theme.palette.text.secondary, 
                    fontSize: '0.75rem' 
                  }}
                >
                  {getUserRole()}
                </Typography>
                <Typography
                  variant="caption"
                  component="button"
                  type="button"
                  title={`${timezone} (${timezoneMetaLabel})`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setTimezoneDialogOpen(true);
                  }}
                  sx={{
                    display: 'block',
                    mt: 0.25,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    textUnderlineOffset: 2,
                    color: 'primary.main',
                    fontSize: '0.75rem',
                    fontFamily: 'inherit',
                    background: 'none',
                    border: 'none',
                    p: 0,
                    textAlign: 'right',
                  }}
                >
                  {timezone}{' '}
                  <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    ({timezoneMetaLabel})
                  </Box>
                </Typography>
              </Box>
              <IconButton
                size="large"
                edge="end"
                aria-label="account of current user"
                aria-controls="primary-search-account-menu"
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                sx={{ 
                  color: theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  }
                }}
              >
                {user.picture ? (
                  <Avatar src={user.picture} sx={{ width: 32, height: 32 }} />
                ) : (
                  <AccountCircleIcon />
                )}
              </IconButton>
              <Menu
                id="primary-search-account-menu"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleProfileMenuClose}
              >
                <MenuItem disabled sx={{ opacity: 1, cursor: 'default' }}>
                  <Typography variant="body2" color="text.secondary">
                    Signed in with {user.auth_provider === 'microsoft' ? 'Microsoft' : 'Google'}
                  </Typography>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleProfileMenuClose();
                    setTimezoneDialogOpen(true);
                  }}
                >
                  <Typography variant="body2" color="text.primary">
                    Timezone: {timezone}{' '}
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      ({timezoneMetaLabel})
                    </Box>
                  </Typography>
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
              <TimezoneSelectorDialog
                open={timezoneDialogOpen}
                onClose={() => setTimezoneDialogOpen(false)}
                currentTimezone={timezone}
                browserTimezone={browserTimezone}
                timezoneOverride={timezoneOverride}
                onSelect={setTimezone}
              />
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? open : true}
        onClose={handleDrawerToggle}
        sx={{
          width: open ? drawerWidth : miniDrawerWidth,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          zIndex: (theme) => theme.zIndex.drawer + 2,
          '& .MuiDrawer-paper': {
            width: open ? drawerWidth : miniDrawerWidth,
            boxSizing: 'border-box',
            backgroundColor: 'background.paper',
            borderRight: '1px solid',
            borderColor: 'divider',
            boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            zIndex: (theme) => theme.zIndex.drawer + 2,
          }
        }}
        ModalProps={{
          keepMounted: true // Better open performance on mobile.
        }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: open ? 'space-between' : 'center',
            px: open ? [1] : 0,
            backgroundColor: theme.palette.mode === 'dark'
              ? theme.palette.background.paper
              : theme.palette.background.default,
            color: theme.palette.text.primary,
            minHeight: 64,
            borderBottom: `1px solid ${theme.palette.divider}`
          }}
        >
          {open ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600,
                    color: theme.palette.primary.main
                  }}
                >
                  CaosBlitz
                </Typography>
                <EnvironmentBadge variant="expanded" />
              </Box>
              <IconButton 
                onClick={handleDrawerToggle} 
                sx={{ 
                  color: theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  }
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
            </>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              <EnvironmentBadge variant="collapsed" />
              <IconButton 
                onClick={handleDrawerToggle}
                sx={{ 
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  }
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          )}
        </Toolbar>
        <Divider />
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <List sx={{ pt: 1, flex: 1 }}>
            {visibleMenuItems.map((item) => (
              <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
                <Tooltip title={open ? '' : item.text} placement="right" arrow>
                  <Link to={item.path} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                    <ListItemButton
                      selected={location.pathname === item.path}
                      onClick={() => {
                        if (isMobile) setOpen(false);
                      }}
                      sx={{
                        mx: open ? 1 : 0.5,
                        borderRadius: 2,
                        mb: 0.5,
                        minHeight: 48,
                        justifyContent: open ? 'initial' : 'center',
                        px: open ? 2 : 1.5,
                        '&.Mui-selected': {
                          backgroundColor: theme.palette.mode === 'dark'
                            ? `${theme.palette.primary.main}20`
                            : `${theme.palette.primary.main}15`,
                          color: theme.palette.primary.main,
                          borderLeft: open ? `3px solid ${theme.palette.primary.main}` : 'none',
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark'
                              ? `${theme.palette.primary.main}30`
                              : `${theme.palette.primary.main}20`,
                          },
                          '& .MuiListItemIcon-root': {
                            color: theme.palette.primary.main,
                          }
                        },
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        }
                      }}
                    >
                      <ListItemIcon sx={{ 
                        color: location.pathname === item.path 
                          ? theme.palette.primary.main 
                          : theme.palette.text.secondary,
                        minWidth: 0,
                        mr: open ? 2 : 'auto',
                        justifyContent: 'center',
                      }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText 
                        primary={item.text}
                        primaryTypographyProps={{
                          color: location.pathname === item.path
                            ? theme.palette.primary.main
                            : theme.palette.text.primary
                        }}
                        sx={{ 
                          opacity: open ? 1 : 0,
                          display: open ? 'block' : 'none',
                        }}
                      />
                    </ListItemButton>
                  </Link>
                </Tooltip>
              </ListItem>
            ))}
          </List>
          <NotificationPanel drawerOpen={open} />
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { 
            xs: '100%',
            md: `calc(100% - ${open ? drawerWidth : miniDrawerWidth}px)` 
          },
          minHeight: '100vh',
          backgroundColor: 'background.default',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar />
        <ActiveAttackBar />
        <Container maxWidth="xl" sx={{ mt: 2 }}>
          <Routes>
            <Route path="/" element={<InitialRoute />} />
            <Route path="/operations" element={<Operations />} />
            <Route path="/maintenance" element={<Maintenance />} />
            <Route path="/partners" element={<Admin />} />
            <Route path="/tenants" element={<Tenants />} />
            <Route path="/runbooks" element={<Runbooks />} />
            <Route path="/executions" element={<Executions />} />
            <Route path="/assets" element={<Assets />} />
          </Routes>
        </Container>
      </Box>
    </Box>
  );
};

const InitialRoute: React.FC = () => {
  const { selectedTenant, availableTenants, setSelectedTenant, loading: tenantLoading } = useTenant();
  const [checkingScenario, setCheckingScenario] = useState(true);
  const [hasActiveScenario, setHasActiveScenario] = useState<boolean | null>(null);

  useEffect(() => {
    let isMounted = true;

    const canCheckScenario = !tenantLoading && (selectedTenant || availableTenants.length === 0);
    if (!canCheckScenario) {
      setCheckingScenario(false);
      return;
    }

    const checkScenario = async () => {
      try {
        const data = await api.getCurrentScenario();
        if (isMounted) {
          setHasActiveScenario(!!data?.scenario);
        }
      } catch (error) {
        console.error('Failed to check active scenario:', error);
        if (isMounted) {
          setHasActiveScenario(false);
        }
      } finally {
        if (isMounted) {
          setCheckingScenario(false);
        }
      }
    };

    setCheckingScenario(true);
    checkScenario();

    return () => {
      isMounted = false;
    };
  }, [tenantLoading, selectedTenant, availableTenants.length]);

  if (tenantLoading || (availableTenants.length === 1 && !selectedTenant) || (selectedTenant && checkingScenario)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (availableTenants.length > 1 && !selectedTenant) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          width: '100%',
          py: 4,
        }}
      >
        <TenantSelectionView
          tenants={availableTenants}
          onSelect={setSelectedTenant}
          title="Select a tenant"
          subtitle="Choose which tenant to work with"
        />
      </Box>
    );
  }

  if (hasActiveScenario) {
    return <Dashboard />;
  }

  return <Navigate to="/runbooks" replace />;
};

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode; user: User | null }> = ({ children, user }) => {
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
};

// Wrapper component to use theme context
const AppContent: React.FC = () => {
  const [open, setOpen] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const { mode } = useThemeMode();

  // Create theme based on user's mode preference
  const theme = useMemo(() => getCaosBlitzTheme(mode), [mode]);

  // Labelled builds prefix the tab title ("[STAGING] CaosBlitz") so a staging tab
  // is still identifiable when several environments are open side by side.
  useEffect(() => {
    document.title = getDocumentTitle('CaosBlitz');
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        if (api.isAuthenticated()) {
          const storedUser = api.getStoredUser();
          if (storedUser) {
            setUser(storedUser);
          } else {
            // Try to get current user from API
            const userPermissions = await api.getCurrentUser();
            setUser(userPermissions.user);
            sessionStorage.setItem('user', JSON.stringify(userPermissions.user));
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        api.logout();
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  useEffect(() => {
    if (!user || !api.isAuthenticated()) {
      return;
    }

    let isMounted = true;
    let refreshTimeout: number | undefined;
    let refreshing = false;

    const refreshAuthToken = async () => {
      if (refreshing || !isMounted || !api.isAuthenticated()) {
        return;
      }
      refreshing = true;
      try {
        const response = await api.refreshToken();
        sessionStorage.setItem('authToken', response.access_token);
        sessionStorage.setItem('user', JSON.stringify(response.user));
      } catch (error) {
        console.error('Token refresh failed:', error);
        api.logout();
        if (isMounted) {
          setUser(null);
        }
      } finally {
        refreshing = false;
      }
    };

    const scheduleRefresh = () => {
      if (!isMounted) return;
      refreshTimeout = window.setTimeout(async () => {
        await refreshAuthToken();
        if (isMounted) {
          scheduleRefresh();
        }
      }, TOKEN_REFRESH_INTERVAL_MS);
    };

    refreshAuthToken();
    scheduleRefresh();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshAuthToken();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMounted = false;
      if (refreshTimeout) {
        window.clearTimeout(refreshTimeout);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user]);

  const handleLogout = () => {
    api.logout();
    setUser(null);
  };

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <NotificationProvider>
        <PermissionProvider>
          <TenantProvider>
            <Router>
              <Routes>
                <Route path="/login" element={user ? <Navigate to="/runbooks" replace /> : <Login />} />
                <Route path="/invite/callback" element={<InviteCallback />} />
                <Route path="/invite/:token" element={<Invite />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route
                  path="/mcp-authorize"
                  element={
                    user ? (
                      <McpAuthorize />
                    ) : (
                      <Navigate
                        to={`/login?returnTo=${encodeURIComponent(`${window.location.pathname}${window.location.search}`)}`}
                        replace
                      />
                    )
                  }
                />
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute user={user}>
                      <MainContent
                        open={open}
                        setOpen={setOpen}
                        user={user}
                        onLogout={handleLogout}
                      />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </Router>
          </TenantProvider>
        </PermissionProvider>
      </NotificationProvider>
    </ThemeProvider>
  );
};

// Main App component with ThemeModeProvider
function App() {
  return (
    <ThemeModeProvider>
      <TimezoneProvider>
        <AppContent />
      </TimezoneProvider>
    </ThemeModeProvider>
  );
}

export default App;
